import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import TodayIcon from '@mui/icons-material/Today';
import { Divider, ListSubheader, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { blue, green, grey, red } from '@mui/material/colors';
import _ from 'lodash';
import React, { act, useContext, useEffect, useState } from 'react';
import { activityApi } from "../enrollment/activity.queries";
import { DialogsContext } from '../contexts';
import { useMenu } from './dialog.util';
import { Status } from "./enums";
import moment from 'moment';

export default function StatusChip(props) {
	const useUpdate = activityApi.useUpdate();
	const menu = useMenu();
	const theme = useTheme();
	const { dialogs } = useContext(DialogsContext);

	// let status = props.status;
	let type = props.type || 'chip';
	let size = props.size;
	let { activity, date } = props;
	let variant, sx, color, icon;

	let [status, setStatus] = useState(props.status);
	useEffect(() => {
		setStatus(props.status);
	}, [props.status]);

	let isSameDay = true;
	if (activity?.startDate && activity?.endDate) {
		isSameDay = moment(activity.startDate).isSame(moment(activity.endDate), 'date');
	}

	let progress = 100;
	let progressDate;
	if (!isSameDay) {
		if (activity?.enrollment?.pace < 1) {
			progress *= activity?.enrollment?.pace;
			if (status == Status.IN_PROGRESS) {
				progressDate = moment(activity.startDate + (activity.endDate - activity.startDate) * activity.completeness / 100);
				if (date?.isSameOrBefore(progressDate, 'date')) {
					status = Status.COMPLETED;
				}
				else {
					status = Status.NOT_STARTED;
				}
			}
		}
	}
	else{
		progress = 50;
	}
	if (activity.completeness) {
		progress += activity.completeness;
		progress = Math.min(100, progress);
	}

	if( Math.round(progress) == 100 ){
		progress = 100;
	}

	if (!status) {
		return;
	}

	const updateStatus = async (updated) => {
		if (activity) {
			// status = updated.status;
			setStatus(updated.status);
			_.assign(activity, updated);
			const result = await useUpdate.mutateAsync(activity);
			props.onChange?.(activity);

			//FIXME put in something that uses eventBus
			if (result._metadata?.prompts?.length) {
				if (result._metadata?.prompts.indexOf('previousIncomplete') >= 0) {
					await dialogs.open('activityCompletePrevious', [activity]);
				}//TODO - this shouldn't be an elseif, but don't have await for the dialog interaction
				else if (result._metadata?.prompts.indexOf('rescheduleEnrollment') >= 0) {
					await dialogs.open('enrollmentReschedulePrompt', [activity.enrollmentId]);
				}
			}

		}
	};

	const notStartedIcon = <CheckCircleOutlineIcon sx={{ color: grey[500], fontSize: size }} />
	const inProgressIcon = <AutorenewIcon sx={{ color: blue[600], fontSize: size }} />
	const completedIcon = <CheckCircleIcon sx={{ color: green[800], fontSize: size }} />
	const skippedIcon = <NotInterestedIcon sx={{ color: red[300], fontSize: size }} />
	const rescheduleIcon = <TodayIcon sx={{ fontSize: size }} />
	const unscheduleIcon = <EventBusyIcon sx={{ fontSize: size }} />

	const getMenu = () => {
		const sx = { ml: 1 };
		const headerSx = { lineHeight: 2, backgroundColor: theme.palette.action.selected };
		return (
			<Menu sx={{ py: 0 }} open={menu.isOpen} onClose={() => menu.close()} anchorEl={menu.anchor} autoFocus={false} disableEnforceFocus>
				<ListSubheader sx={headerSx}>Coursework Actions</ListSubheader>
				{Status.NOT_STARTED != status &&
					<MenuItem onClick={() => { menu.close(); updateStatus({ status: Status.NOT_STARTED, completeness: 0 }) }}>
						{notStartedIcon}
						<Typography sx={sx}>Not Started</Typography>
					</MenuItem>
				}
				{(!(activity.completeness < 100 && Status.COMPLETED == status) && progress < 100) && Status.IN_PROGRESS != status &&
					<MenuItem onClick={() => { menu.close(); updateStatus({ status: Status.IN_PROGRESS, completeness: progress, startDate: activity.startDate ? activity.startDate : new Date().getTime() }) }}>
						{inProgressIcon}
						<Typography sx={sx}>{!isSameDay ? 'Complete Today' : 'Update Progress'}</Typography>
					</MenuItem>
				}
				{(activity.completeness < 100 || Status.COMPLETED != status) &&
					<MenuItem onClick={() => { menu.close(); updateStatus({ status: Status.COMPLETED, endDate: activity.endDate ? activity.endDate : new Date().getTime(), completeness: 100 }) }}>
						{completedIcon}
						<Typography sx={sx}>Complete {!isSameDay && (activity.courseDetail?.type || 'Full Activity')}</Typography>
					</MenuItem>
				}
				{Status.SKIPPED != status &&
					<React.Fragment>
						<Divider />
						<MenuItem onClick={() => { menu.close(); updateStatus({ status: Status.SKIPPED, completeness: 100 }) }}>
							{skippedIcon}
							<Typography sx={sx}>Skip</Typography>
						</MenuItem>
					</React.Fragment>
				}
				{activity.enrollment != null &&
					<React.Fragment>
						<ListSubheader sx={[headerSx, { mt: 2 }]}>Course Actions</ListSubheader>
						<MenuItem onClick={() => { menu.close(); dialogs.open('enrollmentReschedule', activity.enrollment) }}>
							{rescheduleIcon}
							<Typography sx={sx}>Reschedule...</Typography>
						</MenuItem>
						<MenuItem onClick={() => { menu.close(); dialogs.open('enrollmentUnschedule', activity.enrollment) }}>
							{unscheduleIcon}
							<Typography sx={sx}>Unschedule...</Typography>
						</MenuItem>
					</React.Fragment>
				}
			</Menu>
		);
	};

	switch (status) {
		case Status.NOT_STARTED:
			variant = 'outlined';
			icon = notStartedIcon;
			break;
		case Status.IN_PROGRESS:
			if (!isSameDay) {
				color = 'success';
				icon = completedIcon;
			}
			else {
				icon = inProgressIcon;
			}
			break;
		case Status.SKIPPED:
			icon = skippedIcon;
			break;
		case Status.COMPLETED:
		default:
			color = 'success';
			icon = completedIcon;
			break;
	}

	if (type === 'icon') {
		return <React.Fragment>
			<Box sx={{ display: 'flex', cursor: activity ? 'pointer' : 'default' }} onClick={(e) => menu.open(e.target)}>
				{icon}
			</Box>
			{getMenu()}
		</React.Fragment>
	}
	else {
		return <React.Fragment>
			<Chip label={status} size={size} variant={variant} color={color} onClick={(e) => menu.open(e.target)} />
			{getMenu()}
		</React.Fragment>
	}
}